import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (localStorage.getItem("jfl_admin_token")) {
    return <Navigate to="/admin/leads" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("jfl_admin_token", data.access_token);
      nav("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-paper text-ink flex items-center justify-center px-5 py-16"
    >
      <div className="w-full max-w-[440px]">
        <div className="text-center mb-10">
          <img
            src="/brand/jos-logo.png"
            alt="Jos Family Law"
            className="h-11 w-auto mx-auto mb-8 select-none"
            draggable={false}
          />
          <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600">
            Internal Console
          </p>
          <h1 className="mt-3 font-display text-[40px] leading-tight tracking-tight text-ink">
            Sign in to <span className="italic">your dashboard.</span>
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-paper border border-gray-400/30 p-8 space-y-6 shadow-[0_20px_60px_-20px_rgba(35,31,32,0.18)]"
        >
          <div>
            <label
              htmlFor="admin-email"
              className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
            >
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-input-email"
              className="w-full border-b border-gray-400/50 bg-transparent py-3 focus:outline-none focus:border-teal text-ink"
              required
            />
          </div>
          <div>
            <label
              htmlFor="admin-password"
              className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
            >
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-input-password"
              className="w-full border-b border-gray-400/50 bg-transparent py-3 focus:outline-none focus:border-teal text-ink"
              required
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-6 py-4 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="font-mono text-[12px] tracking-[0.24em] uppercase font-semibold">
              {loading ? "Signing in…" : "Sign In"}
            </span>
          </button>
        </form>
      </div>
    </main>
  );
}
